/**
 * Service definition create form — dialog form for creating a new job service
 * definition. Used by ServicesGrid in the service definitions management view.
 */

import { Box, Checkbox, FormControlLabel, TextField } from '@mui/material';
import { useFormik } from 'formik';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';

import ResponsiveDialog from '@sophocles/components/ResponsiveDialog';
import { useCreateServiceMutation } from '@sophocles/features/entities/jobServices';
import { addToast } from '@sophocles/features/toast';

function ServiceForm({ open, onClose, onSubmit }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [createService] = useCreateServiceMutation();

  const validationSchema = useMemo(
    () =>
      yup.object({
        service_type: yup.string().required(t('projectServices.services.validation.serviceTypeRequired')),
        name: yup.string().required(t('projectServices.services.validation.nameRequired')),
        description: yup.string(),
        target_field: yup.string().required(),
        is_visible: yup.bool(),
        is_enabled: yup.bool(),
        is_included_in_change_order: yup.bool(),
        child_services: yup.array().of(yup.number()),
      }),
    [t],
  );

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema,
    initialValues: {
      service_type: 'site',
      name: '',
      description: '',
      target_field: '',
      is_visible: true,
      is_enabled: true,
      is_included_in_change_order: true,
    },
    onSubmit: async values => {
      const result = await createService({ body: values });
      if (result.error) {
        const errors = result.error.data?.errors ?? [];
        dispatch(
          addToast({
            title: t('projectServices.services.toast.error'),
            message: errors.length > 0 ? `Failed to create service: ${errors.join('; ')}` : 'Failed to create service',
            level: 'error',
            dismissible: true,
          }),
        );
      } else {
        dispatch(
          addToast({
            title: t('projectServices.services.toast.success'),
            message: t('projectServices.services.toast.serviceCreated'),
            level: 'success',
            dismissible: true,
          }),
        );
        // eslint-disable-next-line no-use-before-define -- formik <-> handleClose form a submit-time cycle
        handleClose();
        if (onSubmit) onSubmit();
      }
    },
  });

  const handleClose = () => {
    formik.resetForm();
    if (onClose) onClose();
  };

  const canSubmit = !formik.isSubmitting && Object.keys(formik.errors).length === 0;

  return (
    <ResponsiveDialog
      title={t('projectServices.services.dialog.newTitle')}
      open={open}
      canSubmit={canSubmit}
      onClose={handleClose}
      onSubmit={formik.submitForm}
      sx={{
        '& .MuiDialogContent-root > :not(style):not(:last-child)': { mb: 2 },
        '& .MuiDialogContent-root': { py: 3 },
      }}
    >
      <TextField
        required
        fullWidth
        name="name"
        label={t('projectServices.services.form.name')}
        size="small"
        value={formik.values.name}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.touched.name && Boolean(formik.errors.name)}
        helperText={formik.touched.name && formik.errors.name}
      />
      <TextField
        required
        fullWidth
        name="service_type"
        label={t('projectServices.services.form.serviceType')}
        size="small"
        value={formik.values.service_type}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.touched.service_type && Boolean(formik.errors.service_type)}
        helperText={formik.touched.service_type && formik.errors.service_type}
      />
      <TextField
        required
        fullWidth
        name="target_field"
        label={t('projectServices.services.form.targetField')}
        size="small"
        value={formik.values.target_field}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.touched.target_field && Boolean(formik.errors.target_field)}
        helperText={formik.touched.target_field && formik.errors.target_field}
      />
      {/* Filled multiline pads the root, not the textarea, so a short value
          centers vertically in the 3-row slot unless anchored to the top;
          see SettingsTextField for the fuller writeup. */}
      <TextField
        fullWidth
        multiline
        rows={3}
        name="description"
        label={t('projectServices.services.form.description')}
        size="small"
        value={formik.values.description}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        sx={{
          '& .MuiInputBase-root.MuiInputBase-multiline': { alignItems: 'flex-start' },
          '& .MuiInputBase-inputMultiline': { alignSelf: 'flex-start', padding: '0 !important' },
        }}
      />
      <Box>
        <FormControlLabel
          control={<Checkbox name="is_visible" checked={formik.values.is_visible} onChange={formik.handleChange} />}
          label={t('projectServices.services.form.visible')}
        />
        <FormControlLabel
          control={<Checkbox name="is_enabled" checked={formik.values.is_enabled} onChange={formik.handleChange} />}
          label={t('projectServices.services.form.enabled')}
        />
        <FormControlLabel
          control={
            <Checkbox
              name="is_included_in_change_order"
              checked={formik.values.is_included_in_change_order}
              onChange={formik.handleChange}
            />
          }
          label={t('projectServices.services.form.includeInChangeOrders')}
        />
      </Box>
    </ResponsiveDialog>
  );
}

export default ServiceForm;
